(function () {
  'use strict';

  const instances = new WeakMap();

  function initialize(root) {
    if (instances.has(root)) return;

    const controller = new AbortController();
    const { signal } = controller;
    const cleanup = [];
    const animations = new Set();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = (element, event, handler) => {
      if (element) element.addEventListener(event, handler, { signal });
    };
    const animate = (element, distance) => {
      if (!element || reducedMotion.matches || typeof element.animate !== 'function') return;
      element.getAnimations().forEach((animation) => animation.cancel());
      const animation = element.animate(
        [{ opacity: 0.55, transform: 'translateY(' + distance + 'px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: 280, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' }
      );
      animations.add(animation);
      animation.finished.catch(() => {}).finally(() => animations.delete(animation));
    };
    on(reducedMotion, 'change', () => {
      if (reducedMotion.matches) animations.forEach((animation) => animation.cancel());
    });

    const gallery = root.querySelector('[data-gallery]');
    const mainImage = gallery && gallery.querySelector('.ws-main-image');
    const thumbs = gallery ? Array.from(gallery.querySelectorAll('[data-gallery-image]')) : [];
    const caption = root.querySelector('[data-gallery-caption-text]');
    let galleryIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.getAttribute('aria-pressed') === 'true'));
    let imageRevision = 0;

    function selectImage(index, moveFocus) {
      if (!mainImage || !thumbs[index]) return;
      const thumb = thumbs[index];
      const source = thumb.dataset.galleryImage;
      if (!source) return;
      galleryIndex = index;
      thumbs.forEach((item, position) => {
        item.classList.toggle('is-active', position === index);
        item.setAttribute('aria-pressed', String(position === index));
      });
      if (moveFocus) thumb.focus();
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.src = source;
      mainImage.alt = thumb.dataset.galleryAlt || '';
      if (caption) caption.textContent = thumb.dataset.galleryCaption || '';
      const revision = ++imageRevision;
      const ready = typeof mainImage.decode === 'function' ? mainImage.decode() : Promise.resolve();
      ready.catch(() => {}).then(() => {
        if (!signal.aborted && revision === imageRevision) animate(mainImage, 5);
      });
    }

    thumbs.forEach((thumb, index) => {
      on(thumb, 'click', () => selectImage(index, false));
      on(thumb, 'keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % thumbs.length;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + thumbs.length) % thumbs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = thumbs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        selectImage(next, true);
      });
    });

    const tabs = Array.from(root.querySelectorAll('[data-style-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-style-panel]'));
    const styleCount = root.querySelector('[data-style-count]');
    const styleProgress = root.querySelector('[data-style-progress]');
    const stage = root.querySelector('[data-style-stage]');
    let styleIndex = Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));

    function selectStyle(index, moveFocus, withMotion) {
      if (!tabs.length) return;
      styleIndex = (index + tabs.length) % tabs.length;
      const selected = tabs[styleIndex];
      tabs.forEach((tab, position) => {
        const active = position === styleIndex;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        const active = panel.id === selected.getAttribute('aria-controls');
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
        if (active && withMotion) animate(panel, 9);
      });
      if (styleCount) styleCount.textContent = String(styleIndex + 1).padStart(2, '0') + ' / ' + String(tabs.length).padStart(2, '0');
      if (styleProgress) styleProgress.style.width = ((styleIndex + 1) / tabs.length * 100) + '%';
      if (moveFocus) selected.focus();
    }

    tabs.forEach((tab, index) => {
      on(tab, 'click', () => selectStyle(index, false, true));
      on(tab, 'keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight') next = index + 1;
        if (event.key === 'ArrowLeft') next = index - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        selectStyle(next, true, true);
      });
    });
    on(root.querySelector('[data-style-prev]'), 'click', () => selectStyle(styleIndex - 1, false, true));
    on(root.querySelector('[data-style-next]'), 'click', () => selectStyle(styleIndex + 1, false, true));
    selectStyle(styleIndex, false, false);

    let gesture;
    on(stage, 'pointerdown', (event) => {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
    });
    on(stage, 'pointerup', (event) => {
      if (!gesture || gesture.id !== event.pointerId) return;
      const deltaX = event.clientX - gesture.x;
      const deltaY = event.clientY - gesture.y;
      gesture = undefined;
      if (Math.abs(deltaX) >= 50 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
        selectStyle(styleIndex + (deltaX < 0 ? 1 : -1), false, true);
      }
    });
    on(stage, 'pointercancel', () => { gesture = undefined; });

    const dialog = root.querySelector('[data-zoom-dialog]');
    const zoomImage = dialog && dialog.querySelector('.ws-zoom-image');
    const zoomOpener = root.querySelector('[data-zoom-open]');
    let previousOverflow;
    let restoreFocus;
    let scrollLocked = false;

    function unlockScroll() {
      if (!scrollLocked) return;
      document.documentElement.style.overflow = previousOverflow;
      scrollLocked = false;
    }

    on(zoomOpener, 'click', () => {
      if (!dialog || !zoomImage || !mainImage || dialog.open || typeof dialog.showModal !== 'function') return;
      zoomImage.removeAttribute('srcset');
      zoomImage.removeAttribute('sizes');
      zoomImage.src = thumbs[galleryIndex] ? thumbs[galleryIndex].dataset.galleryImage : mainImage.src;
      zoomImage.alt = mainImage.alt;
      restoreFocus = document.activeElement;
      dialog.showModal();
      previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      scrollLocked = true;
    });
    on(root.querySelector('[data-zoom-close]'), 'click', () => {
      if (dialog && dialog.open) dialog.close();
    });
    on(dialog, 'click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    on(dialog, 'close', () => {
      unlockScroll();
      if (restoreFocus && restoreFocus.isConnected) restoreFocus.focus({ preventScroll: true });
    });
    cleanup.push(() => {
      if (dialog && dialog.open) dialog.close();
      unlockScroll();
    });

    const video = root.querySelector('[data-studio-video]');
    const videoToggle = root.querySelector('[data-video-toggle]');
    if (video && videoToggle) {
      const updateVideoButton = () => {
        const playing = !video.paused && !video.ended;
        videoToggle.textContent = playing ? videoToggle.dataset.pauseLabel : videoToggle.dataset.playLabel;
        videoToggle.setAttribute('aria-pressed', String(playing));
      };
      on(videoToggle, 'click', async () => {
        if (video.paused || video.ended) {
          try { await video.play(); } catch (_) { /* Native controls remain available if playback is blocked. */ }
        } else {
          video.pause();
        }
        updateVideoButton();
      });
      ['play', 'pause', 'ended', 'error', 'emptied'].forEach((event) => on(video, event, updateVideoButton));
      on(document, 'visibilitychange', () => {
        if (document.hidden) video.pause();
      });
      cleanup.push(() => video.pause());
      updateVideoButton();
    }

    const sticky = root.querySelector('[data-sticky-purchase]');
    const purchase = root.querySelector('[data-main-purchase]');
    const mobile = window.matchMedia('(max-width: 760px)');
    let purchasePassed = false;
    const updateSticky = () => {
      if (sticky) sticky.hidden = !mobile.matches || !purchasePassed;
    };
    if (sticky && purchase && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        purchasePassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        updateSticky();
      }, { threshold: 0 });
      observer.observe(purchase);
      on(mobile, 'change', updateSticky);
      cleanup.push(() => observer.disconnect());
    }

    instances.set(root, () => {
      controller.abort();
      cleanup.forEach((dispose) => dispose());
      animations.forEach((animation) => animation.cancel());
      instances.delete(root);
    });
  }

  function rootsWithin(scope) {
    const roots = Array.from(scope.querySelectorAll('[data-studio]'));
    if (scope.matches && scope.matches('[data-studio]')) roots.unshift(scope);
    return roots;
  }

  const start = () => rootsWithin(document).forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();

  document.addEventListener('shopify:section:load', (event) => rootsWithin(event.target).forEach(initialize));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsWithin(event.target).forEach((root) => {
      const dispose = instances.get(root);
      if (dispose) dispose();
    });
  });

  // Keep the original quantity minimum for the native product and cart forms.
  document.addEventListener('change', (event) => {
    if (event.target.matches('[name=quantity]') && Number(event.target.value) < 1) event.target.value = 1;
  });
})();
